export interface Installment {
  installment_number: string;
  due_date: string;
  installment_amount: number;
}

export interface InstallmentPlan {
  plan_type: string;
  number_of_installments: number;
  meta?: {
    guarantor_required?: string;
  };
  notes?: {
    note?: string;
  };
  plan_installments: Installment[];
  duration?: number;
}

export interface InstallmentDetail {
  number: number | string;
  payBefore: string;
  amountDue: string;
}

export interface GetPlan {
  title: string;
  description: string;
  installments: InstallmentDetail[];
  planType: string;
  duration?: number;
  duration_unit?: string;
  notes?: {
    note?: string;
  };
}

export const getGuarantorDescription = (plan: InstallmentPlan, t: any): string => {
  const type = plan.meta?.guarantor_required;
  switch (type) {
    case "local_without_third_party":
      return t("propertypage.components.instalment.guarantor.local_without_third_party");
    case "local_with_third_party":
      return t("propertypage.components.instalment.guarantor.local_with_third_party");
    case "international_local":
      return t("propertypage.components.instalment.guarantor.international_local");
    case "false":
      return t("propertypage.components.instalment.guarantor.false");
    case "information_unavailable":
      return t("propertypage.components.instalment.guarantor.information_unavailable");
    default:
      return t(plan.notes?.note) || "";
  }
};

export const getPlanTitle = (plan: InstallmentPlan, t: any): string => {
  switch (plan.plan_type) {
    case "fixed":
      return plan.number_of_installments === 0
        ? t("propertypage.components.instalment.plan.fixed_single")
        : t("propertypage.components.instalment.plan.fixed_multiple", {
            count: plan.number_of_installments,
          });
    case "weekly":
      return t("propertypage.components.instalment.plan.weekly");
    case "fortnightly":
      return t("propertypage.components.instalment.plan.fortnightly");
    case "monthly":
      return t("propertypage.components.instalment.plan.monthly");
    default:
      return t("propertypage.components.instalment.plan.default");
  }
};

export const getInstallmentDetails = (plan: InstallmentPlan): InstallmentDetail[] => {
  if (plan.plan_type === "fixed") {
    return plan.plan_installments.length > 0
      ? plan.plan_installments.map((installment) => ({
          number: parseInt(installment.installment_number.split(" ")[1], 10),
          payBefore: new Date(installment.due_date).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          amountDue: `${installment.installment_amount}%`,
        }))
      : [
          {
            number: 1,
            payBefore: "To be confirmed",
            amountDue: "100%",
          },
        ];
  }

  let frequencyText = "";
  let amountDue = "";
  switch (plan.plan_type) {
    case "weekly":
      frequencyText = "Every week";
      amountDue = plan.duration ? `${(100 / plan.duration).toFixed(2)}%` : "";
      break;
    case "fortnightly":
      frequencyText = "Every 2 weeks";
      amountDue = plan.duration ? `${(100 / (plan.duration / 2)).toFixed(2)}%` : "";
      break;
    case "monthly":
      frequencyText = "Every month";
      amountDue = plan.duration ? `${(100 / (plan.duration / 4)).toFixed(2)}%` : "";
      break;
    default:
      frequencyText = "";
      amountDue = "100%";
  }

  return [
    {
      number: plan.plan_type.charAt(0).toUpperCase() + plan.plan_type.slice(1),
      payBefore: frequencyText,
      amountDue,
    },
  ];
};

export const getInstallmentPlans = (
  plans: any,
  duration: string,
  academic_year: string,
  t: any,
): GetPlan[] =>
  plans
    .filter((plan: any) => (academic_year === "All" ? plan : plan.academic_year === academic_year))
    .filter((plan: any) =>
      duration === "All" ? plan : `${plan?.duration} ${plan?.duration_unit}` === duration,
    )
    .map((plan: any) => ({
      title: getPlanTitle(plan, t),
      description: getGuarantorDescription(plan, t),
      installments: getInstallmentDetails(plan),
      planType: plan.plan_type,
      duration: plan.duration,
      duration_unit: plan.duration_unit,
    }));

export const unitTypes: any = {
  all: "All",
  with_guarantor: "With Guarantor",
  without_guarantor: "Without Guarantor",
};

export const getInstallmentPlansByGuarantorType = (
  plans: any,
  duration: string,
  academic_year: string,
  guarantorType: string,
  t: any,
): GetPlan[] => {
  const filteredPlans = plans
    .filter((plan: any) => (academic_year === "All" ? plan : plan.academic_year === academic_year))
    .filter((plan: any) =>
      duration === "All" ? plan : `${plan?.duration} ${plan?.duration_unit}` === duration,
    );

  let result: GetPlan[] = [];

  if (guarantorType === "all") {
    result = filteredPlans.map((plan: any) => ({
      title: getPlanTitle(plan, t),
      description: getGuarantorDescription(plan, t),
      installments: getInstallmentDetails(plan),
      planType: plan.plan_type,
      duration: plan.duration,
      duration_unit: plan.duration_unit,
      notes: plan.notes,
    }));
  } else if (guarantorType === "without_guarantor") {
    result = filteredPlans
      .filter((plan: any) => plan.meta?.guarantor_required === "false")
      .map((plan: any) => ({
        title: getPlanTitle(plan, t),
        description: getGuarantorDescription(plan, t),
        installments: getInstallmentDetails(plan),
        planType: plan.plan_type,
        duration: plan.duration,
        duration_unit: plan.duration_unit,
        notes: plan.notes,
      }));
  } else if (guarantorType === "with_guarantor") {
    result = filteredPlans
      .filter(
        (plan: any) =>
          plan.meta?.guarantor_required &&
          plan.meta?.guarantor_required !== "false" &&
          plan.meta?.guarantor_required !== "information_unavailable",
      )
      .map((plan: any) => ({
        title: getPlanTitle(plan, t),
        description: getGuarantorDescription(plan, t),
        installments: getInstallmentDetails(plan),
        planType: plan.plan_type,
        duration: plan.duration,
        duration_unit: plan.duration_unit,
        notes: plan.notes,
      }));
  }

  return result.sort((a: GetPlan, b: GetPlan) => {
    const planTypeOrder: Record<string, number> = {
      weekly: 1,
      fortnightly: 2,
      monthly: 3,
      fixed: 4,
    };

    const orderA = planTypeOrder[a.planType as keyof typeof planTypeOrder];
    const orderB = planTypeOrder[b.planType as keyof typeof planTypeOrder];

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    if (a.planType === "fixed" && b.planType === "fixed") {
      const numA = parseInt(a.title.split(" ")[0], 10);
      const numB = parseInt(b.title.split(" ")[0], 10);
      return numB - numA;
    }

    return 0;
  });
};

export const getInstallmentTabData = (plans: any[]) => {
  const all = plans || [];

  const withGuarantor = plans.filter(
    (plan: any) =>
      plan.meta?.guarantor_required &&
      plan.meta?.guarantor_required !== "false" &&
      plan.meta?.guarantor_required !== "information_unavailable",
  );

  const withoutGuarantor = plans.filter((plan: any) => plan.meta?.guarantor_required === "false");

  return {
    all,
    with_guarantor: withGuarantor,
    without_guarantor: withoutGuarantor,
  };
};

export const safeToFixed = (value: number, digits = 1): number => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : Number(num.toFixed(digits));
};
